import { Injectable } from '@angular/core';
import { addressStringShort } from '@core/util/address';
import { GetPrimaryTitle } from '@core/util/matter';
import { Business } from '@models/businesses';
import { BusinessClient, ClientType } from '@models/clients';
import {
	Matter,
	PersonMatterClient,
	BusinessMatterClient,
	MatterTitle,
} from '@models/matter';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { cloneDeep } from 'lodash';
import { BaseFormMapper } from './base-form-mapper';
import { NameFormattingSettings } from '@models/businessSettings/settings';

@Injectable({
	providedIn: 'root',
})
export class ON_GST_NEW_REBATE_RENTAL_Mapper extends BaseFormMapper<ON_GST_NEW_REBATE_RENTAL> {
	CloserToFormMappings = Mappings;

	mergeFormData(
		formData: ON_GST_NEW_REBATE_RENTAL,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
		matterTitles: MatterTitle[],
	) {
		const _formData = formData
			? cloneDeep(formData)
			: ({} as ON_GST_NEW_REBATE_RENTAL);

		let primary = buyers?.find((x) => x.primaryContact);
		if (!primary && buyers?.length > 0) {
			primary = buyers[0];
		}

		if (!(_formData?.claimantLanguage?.length > 0)) {
			_formData.claimantLanguage = '0';
		}

		if (!(_formData?.claimantLegalName?.length > 0) && primary) {
			_formData.claimantLegalName = this.getClientsName(primary);
		}

		if (primary?.type !== ClientType.PERSON) {
			if (
				!(_formData?.claimantBusinessNumber1?.length > 0) &&
				(primary as BusinessClient)?.corporationNumber?.length >= 9
			) {
				_formData.claimantBusinessNumber1 = (
					primary as BusinessClient
				)?.corporationNumber?.substring(0, 9);
			}

			if (
				!(_formData?.claimantBusinessNumber2?.length > 0) &&
				(primary as BusinessClient)?.corporationNumber?.length >= 13
			) {
				_formData.claimantBusinessNumber2 = (
					primary as BusinessClient
				)?.corporationNumber?.substring(9, 13);
			}
		}

		if (buyers?.length > 0) {
			const others = buyers?.filter((x) => x.id !== primary?.id);

			if (others?.length > 0) {
				_formData.ownerTwoLegalName = this.getClientsName(others[0]);
			}

			if (others?.length > 1) {
				_formData.ownerThreeLegalName = this.getClientsName(others[1]);
			}
		}

		const primaryAddress =
			primary?.type === ClientType.PERSON
				? primary?.reportAddress
				: primary?.serviceAddress;
		const address = primaryAddress ?? matter?.propertyDetails?.address;

		if (!(_formData?.claimantAddress?.length > 0) && address) {
			_formData.claimantAddress = addressStringShort(address);
		}

		if (!(formData?.claimantCity?.length > 0) && address?.city) {
			_formData.claimantCity = address?.city;
		}

		if (!(formData?.claimantProvince?.length > 0) && address?.province) {
			_formData.claimantProvince = address?.province;
		}

		if (!(formData?.claimantPostalCode?.length > 0) && address?.postcode) {
			_formData.claimantPostalCode = address?.postcode?.replace(/ /g, '');
		}

		if (
			!(_formData?.propertyAddress?.length > 0) &&
			matter?.propertyDetails?.address
		) {
			_formData.propertyAddress = addressStringShort(
				matter?.propertyDetails?.address,
			);
		}

		if (
			!(formData?.propertyCity?.length > 0) &&
			matter?.propertyDetails?.address?.city
		) {
			_formData.propertyCity = matter?.propertyDetails?.address?.city;
		}

		if (
			!(formData?.propertyProvince?.length > 0) &&
			matter?.propertyDetails?.address?.province
		) {
			_formData.propertyProvince = matter?.propertyDetails?.address?.province;
		}

		if (
			!(formData?.propertyPostalCode?.length > 0) &&
			matter?.propertyDetails?.address?.postcode
		) {
			_formData.propertyPostalCode =
				matter?.propertyDetails?.address?.postcode?.replace(/ /g, '');
		}

		if (
			!(formData?.propertyPossessionDate?.length > 0) &&
			matter?.closingDate
		) {
			_formData.propertyPossessionDate = matter?.closingDate?.replace(/-/g, '');
		}

		const primaryPropertyTitle = GetPrimaryTitle(matterTitles);

		if (
			!(formData?.propertyLot?.length > 0) &&
			primaryPropertyTitle &&
			primaryPropertyTitle[1]?.lot
		) {
			_formData.propertyLot = primaryPropertyTitle[1].lot;
		}

		if (
			!(formData?.propertyPlan?.length > 0) &&
			primaryPropertyTitle &&
			primaryPropertyTitle[1]?.plan
		) {
			_formData.propertyPlan = primaryPropertyTitle[1].plan;
		}

		if (
			!(formData?.propertyOther?.length > 0) &&
			primaryPropertyTitle &&
			(primaryPropertyTitle[1]?.section || primaryPropertyTitle[1]?.parcel)
		) {
			const propertyOther = [];
			if (primaryPropertyTitle[1]?.section) {
				propertyOther.push(primaryPropertyTitle[1]?.section);
			}
			if (primaryPropertyTitle[1]?.parcel) {
				propertyOther.push(primaryPropertyTitle[1]?.parcel);
			}
			_formData.propertyOther = propertyOther.join(',');
		}

		return _formData;
	}

	MapValues(): Partial<ON_GST_NEW_REBATE_RENTAL> {
		return {};
	}
}

const Mappings: ON_GST_NEW_REBATE_RENTAL = {
	claimantBusinessNumber1:
		'form1[0].Page1[0].PartA[0].PartA_BusinessNumber[0].BusinessNumber_RT1[0]',
	claimantBusinessNumber2:
		'form1[0].Page1[0].PartA[0].PartA_BusinessNumber[0].BusinessNumber_RT2[0]',
	claimantSocial: 'form1[0].Page1[0].PartA[0].PartA_SIN[0].SIN_Comb[0]',
	claimantLanguage: 'form1[0].Page1[0].PartA[0].PartA_Language[0]',
	claimantLegalName: 'form1[0].Page1[0].PartA[0].PartA_Claimants_Legal_Name[0]',
	ownerTwoLegalName:
		'form1[0].Page1[0].PartA[0].PartA_Owner2[0].Owmer2_Name[0]',
	ownerThreeLegalName:
		'form1[0].Page1[0].PartA[0].PartA_Owner3[0].Owner3_Name[0]',
	claimantAddress:
		'form1[0].Page1[0].PartA[0].PartA_MailingAddress[0].MailingAddress[0]',
	claimantCity: 'form1[0].Page1[0].PartA[0].PartA_MailingAddress[0].City[0]',
	claimantProvince:
		'form1[0].Page1[0].PartA[0].PartA_MailingAddress[0].Province[0]',
	claimantPostalCode:
		'form1[0].Page1[0].PartA[0].PartA_MailingAddress[0].PostalCode_Comb_BordersAll[0].PostalCode[0]',
	claimantContactLastName:
		'form1[0].Page2[0].PartA[0].PartA_ContactPerson[0].LastName[0]',
	claimantContactFirstName:
		'form1[0].Page2[0].PartA[0].PartA_ContactPerson[0].FirstName[0]',
	claimantContactPhone:
		'form1[0].Page2[0].PartA[0].PartA_ContactPerson[0].TelephoneNumber[0]',
	claimantContactPhoneExt:
		'form1[0].Page2[0].PartA[0].PartA_ContactPerson[0].Extension[0]',
	ownerName: 'form1[0].Page2[0].PartA[0].PartA_OwnerInfo[0].Name[0]',
	ownerSocial:
		'form1[0].Page2[0].PartA[0].PartA_OwnerInfo[0].SIN[0].SIN_Comb[0]',
	ownerTitle: 'form1[0].Page2[0].PartA[0].PartA_OwnerInfo[0].Title[0]',
	ownerPhone:
		'form1[0].Page2[0].PartA[0].PartA_OwnerInfo[0].TelephoneNumber[0]',
	ownerPhoneExt: 'form1[0].Page2[0].PartA[0].PartA_OwnerInfo[0].Extension[0]',
	propertyAddress:
		'form1[0].Page2[0].PartB[0].PartB_Address_Leaseed[0].PartB_Address[0]',
	propertyCity:
		'form1[0].Page2[0].PartB[0].PartB_Address_Leaseed[0].PartB_City[0]',
	propertyProvince:
		'form1[0].Page2[0].PartB[0].PartB_Address_Leaseed[0].PartB_Province[0]',
	propertyPostalCode:
		'form1[0].Page2[0].PartB[0].PartB_Address_Leaseed[0].PartB_PostalCode[0].PostalCode[0]',
	propertyRelevantDate:
		'form1[0].Page2[0].PartB[0].PartB_Address_Leaseed[0].PartB_Relevantdate[0].DateYYYYMMDD_Comb[0]',
	propertyPossessionDate:
		'form1[0].Page2[0].PartB[0].PartB_Address_Leaseed[0].PartB_PossDate[0].DateYYYYMMDD_Comb[1]',
	propertyLot:
		'form1[0].Page3[0].PartB[0].PartB_LegalDescrip[0].PartB_Lot_Stata_Num[0]',
	propertyPlan:
		'form1[0].Page3[0].PartB[0].PartB_LegalDescrip[0].PartB_PlanNumber[0]',
	propertyOther:
		'form1[0].Page3[0].PartB[0].PartB_LegalDescrip[0].PartB_Other[0]',
	propertyManufacturer:
		'form1[0].Page3[0].PartB[0].PartB_LegalDescrip[0].PartB_Manufacturer[0]',
	propertyModel:
		'form1[0].Page3[0].PartB[0].PartB_LegalDescrip[0].PartB_Model[0]',
	propertySerial:
		'form1[0].Page3[0].PartB[0].PartB_LegalDescrip[0].PartB_Serial_Number[0]',
	claimantType:
		'form1[0].Page3[0].PartC[0].PartC_ClaimantType[0].PartC_ClaimantTypeListe[0].RadioButtonList[0]',
	claimantPurchaseAgreementDate:
		'form1[0].Page3[0].PartC[0].PartC_ClaimantType[0].PartC_PurchaseAgreeDate[0].DateYYYYMMDD_Comb[0]',
	constructionType:
		'form1[0].Page3[0].PartC[0].PartC_Type_Of_Construction[0].PartC_ConstructionTypeListe[0].RadioButtonList2[0]',
	housingType:
		'form1[0].Page4[0].PartC[0].PartC_HouseType[0].PartC_TypeHousing[0].PartC_HouseTypeListe[0].RadioButtonList3[0]',
	housingAdjustFromDate:
		'form1[0].Page4[0].PartC[0].PartC_HouseType[0].PartC_ClaimAdjust[0].PartC_AdjustDate[0].FromDate[0]',
	housingAdjustToDate:
		'form1[0].Page4[0].PartC[0].PartC_HouseType[0].PartC_ClaimAdjust[0].PartC_AdjustDate[0].ToDate[0]',
	housingApplicationTypeSix:
		'form1[0].Page4[0].PartC[0].PartC_HouseType[0].PartC_ApplicationType[0].ApplicationType[0]',
	housingApplicationTypeSeven:
		'form1[0].Page4[0].PartC[0].PartC_HouseType[0].PartC_ApplicationType[0].ApplicationType[1]',
	housingApplicationTypeEight:
		'form1[0].Page4[0].PartC[0].PartC_HouseType[0].PartC_ApplicationType[0].ApplicationType[2]',
	housingApplicationTypeNineA:
		'form1[0].Page4[0].PartC[0].PartC_HouseType[0].PartC_ApplicationType[0].ApplicationType[3]',
	housingApplicationTypeNineB:
		'form1[0].Page4[0].PartC[0].PartC_HouseType[0].PartC_ApplicationType[0].ApplicationType[4]',
	rebateType6And7LineA:
		'form1[0].Page5[0].PartD[0].Section1[0].PartD_Section1_LineA[0].Section1_LineA[0].Section1_Amount_LineA[0]',
	rebateType6And7LineB:
		'form1[0].Page5[0].PartD[0].Section1[0].PartD_Section1_LineB[0].Section1_LineB[0].Section1_Amount_LineB[0]',
	rebateType6And7LineC:
		'form1[0].Page5[0].PartD[0].Section1[0].PartD_Section1_LineC[0].Section1_LineC[0].Section1_Amount_LineC[0]',
	rebateType6And7GSTSelectionOne:
		'form1[0].Page6[0].PartD[0].Section1[0].PartD_Section1_LineD[0].PartD_Section1_LineD_IfPaid[0]',
	rebateType6And7GSTSelectionTwo:
		'form1[0].Page6[0].PartD[0].Section1[0].PartD_Section1_LineD[0].PartD_Section1_LineD_IfPaid[1]',
	rebateType6And7GSTSelectionThree:
		'form1[0].Page6[0].PartD[0].Section1[0].PartD_Section1_LineD[0].PartD_Section1_LineD_IfPaid[2]',
	rebateType6And7LineD:
		'form1[0].Page6[0].PartD[0].Section1[0].PartD_Section1_LineD[0].Section1_LineD[0].Section1_Amount_LineD[0]',
	rebateType6And7LineE:
		'form1[0].Page6[0].PartD[0].Section1[0].PartD_Section1_LineE[0].Section1_LineE[0].Section1_Amount_LineE[0]',
	rebateType6RentalProperty:
		'form1[0].Page6[0].PartD[0].Section2[0].PartD_Section2_LineF[0].Section2_LineF[0].Section2_Amount_LineF[0]',
	rebateType6ProvincialRental:
		'form1[0].Page6[0].PartD[0].Section2[0].PartD_Section2_LineG[0].Section2_LineG[0].Section2_Amount_LineG[0]',
	rebateType6TotalAmount:
		'form1[0].Page6[0].PartD[0].Section2[0].PartD_Section2_LineH[0].Section2_LineH[0].Section2_Amount_LineH[0]',
	rebateType7SalePriceBuildingOnly:
		'form1[0].Page6[0].PartD[0].Section3[0].PartD_Section3_LineI[0].Section3_LineI[0].Section3_Amount_LineI[0]',
	rebateType7PurchaseDate:
		'form1[0].Page6[0].PartD[0].Section3[0].PartD_Section3_DatePurchase[0].Section3_PurchaseDate[0].DateYYYYMMDD_Comb[0]',
	rebateType7LineI:
		'form1[0].Page6[0].PartD[0].Section3[0].PartD_Section3_LineJ[0].Section3_AmountFromLineI_LineJ[0].Section3_Amount_LineI[0]',
	rebateType7RebateRate:
		'form1[0].Page6[0].PartD[0].Section3[0].PartD_Section3_LineJ[0].PartD_Section3_RebateRate[0]',
	rebateType7MaximumRebate:
		'form1[0].Page6[0].PartD[0].Section3[0].PartD_Section3_LineJ[0].Section3_LineJ[0].Section3_Amount_LineJ[0]',
	rebateType7RebateSituation:
		'form1[0].Page6[0].PartD[0].Section3[0].PartD_Section3_LineJ[0].Section3_RebateSituation[0]',
	rebateType7LineKOptionOne:
		'form1[0].Page7[0].PartD[0].Section3[0].PartD_Section3_Linek[0].Section3_CalcInfo_LineK_Option1[0].PartD_Section3_LineK_Option[0]',
	rebateType7LineKOptionTwo:
		'form1[0].Page7[0].PartD[0].Section3[0].PartD_Section3_Linek[0].Section3_CalcInfo_LineK_Option2[0].PartD_Section3_LineK_Option[0]',
	rebateType7LineKOptionThree:
		'form1[0].Page7[0].PartD[0].Section3[0].PartD_Section3_Linek[0].Section3_CalcInfo_LineK_Option3[0].PartD_Section3_LineK_Option[0]',
	rebateType7LineKOptionFour:
		'form1[0].Page7[0].PartD[0].Section3[0].PartD_Section3_Linek[0].Section3_CalcInfo_LineK_Option4[0].PartD_Section3_LineK_Option[0]',
	rebateType7LineKOptionFive:
		'form1[0].Page7[0].PartD[0].Section3[0].PartD_Section3_Linek[0].Section3_CalcInfo_LineK_Option5[0].PartD_Section3_LineK_Option[0]',
	rebateType7LineKOptionSix:
		'form1[0].Page7[0].PartD[0].Section3[0].PartD_Section3_Linek[0].Section3_CalcInfo_LineK_Option6[0].PartD_Section3_LineK_Option[0]',
	rebateType7LineKAmount:
		'form1[0].Page7[0].PartD[0].Section3[0].PartD_Section3_Linek[0].Section3_LineK[0].Section3_Amount_LineK[0]',
	rebateType7RentalAmount:
		'form1[0].Page8[0].PartD[0].PartD_Section3_LineL[0].Section3_LineL[0].Section3_Amount_LineL[0]',
	rebateType7ProvincialRentalAmount:
		'form1[0].Page8[0].PartD[0].PartD_Section3_LineM[0].Section3_LineM[0].Section3_Amount_LineM[0]',
	rebateType7TotalRebateAmount:
		'form1[0].Page8[0].PartD[0].PartD_Section3_LineN[0].Section3_LineN[0].Section3_Amount_LineN[0]',
	rebateType9AAnd9BLineO:
		'form1[0].Page8[0].PartE[0].PartE_LineO[0].PartE_LineO[0].PartE_Amount_LineO[0]',
	rebateType9AAnd9BLandValue:
		'form1[0].Page8[0].PartE[0].PartE_LineP[0].PartE_LineP[0].PartE_Amount_LineP[0]',
	rebateType9AAnd9BLineQ:
		'form1[0].Page8[0].PartE[0].PartE_LineQ[0].PartE_LineQ[0].PartE_Amount_LineQ[0]',
	rebateType9AAnd9BLineR:
		'form1[0].Page8[0].PartE[0].PartE_LineR[0].PartE_Sep_LineR[0]',
	rebateType9AAnd9BLineS:
		'form1[0].Page8[0].PartE[0].PartE_LineS[0].PartE_LineS[0].PartE_Amount_LineQ[0]',
	rebateType9AAnd9BCalculationOne:
		'form1[0].Page9[0].PartE[0].PartE_LineT[0].PartE_Calculation_LineT[0]',
	rebateType9AAnd9BCalculationTwo:
		'form1[0].Page9[0].PartE[0].PartE_LineT[0].PartE_Calculation_LineT[1]',
	rebateType9AAnd9BCalculationAmount:
		'form1[0].Page9[0].PartE[0].PartE_LineT[0].PartE_LineT[0].PartE_Amount_LineT[0]',
	rebateType9AAnd9BLineU:
		'form1[0].Page9[0].PartE[0].PartE_LineU[0].PartE_LineU[0].PartE_Amount_LineU[0]',
	rebateType9AAnd9BTotalRebateAmount:
		'form1[0].Page9[0].PartE[0].PartE_LineV[0].PartE_LineV[0].PartE_Amount_LineV[0]',
	certificationName: 'form1[0].Page9[0].PartF[0].PartF_Name[0]',
	certificationDate:
		'form1[0].Page9[0].PartF[0].PartF_Date[0].DateYYYYMMDD_Comb[0]',
	directDepositBranch:
		'form1[0].Page10[0].PartG[0].PartG_BranchNum[0].BranchNumber[0]',
	directDepositInstitution:
		'form1[0].Page10[0].PartG[0].PartG_InstitNum[0].InstitutionNumber[0]',
	directDepositAccount:
		'form1[0].Page10[0].PartG[0].PartG_AccountNum[0].AccountNumber[0]',
	directDepositAccountHolder:
		'form1[0].Page10[0].PartG[0].PartG_AccountName[0].Name[0]',
};

export interface ON_GST_NEW_REBATE_RENTAL {
	claimantBusinessNumber1: string;
	claimantBusinessNumber2: string;
	claimantSocial: string;
	claimantLanguage: string;
	claimantLegalName: string;
	ownerTwoLegalName: string;
	ownerThreeLegalName: string;
	claimantAddress: string;
	claimantCity: string;
	claimantProvince: string;
	claimantPostalCode: string;
	claimantContactLastName: string;
	claimantContactFirstName: string;
	claimantContactPhone: string;
	claimantContactPhoneExt: string;
	ownerName: string;
	ownerSocial: string;
	ownerTitle: string;
	ownerPhone: string;
	ownerPhoneExt: string;
	propertyAddress: string;
	propertyCity: string;
	propertyProvince: string;
	propertyPostalCode: string;
	propertyRelevantDate: string;
	propertyPossessionDate: string;
	propertyLot: string;
	propertyPlan: string;
	propertyOther: string;
	propertyManufacturer: string;
	propertyModel: string;
	propertySerial: string;
	claimantType: string;
	claimantPurchaseAgreementDate: string;
	constructionType: string;
	housingType: string;
	housingAdjustFromDate: string;
	housingAdjustToDate: string;
	housingApplicationTypeSix: string;
	housingApplicationTypeSeven: string;
	housingApplicationTypeEight: string;
	housingApplicationTypeNineA: string;
	housingApplicationTypeNineB: string;
	rebateType6And7LineA: string;
	rebateType6And7LineB: string;
	rebateType6And7LineC: string;
	rebateType6And7GSTSelectionOne: string;
	rebateType6And7GSTSelectionTwo: string;
	rebateType6And7GSTSelectionThree: string;
	rebateType6And7LineD: string;
	rebateType6And7LineE: string;
	rebateType6RentalProperty: string;
	rebateType6ProvincialRental: string;
	rebateType6TotalAmount: string;
	rebateType7SalePriceBuildingOnly: string;
	rebateType7PurchaseDate: string;
	rebateType7LineI: string;
	rebateType7RebateRate: string;
	rebateType7MaximumRebate: string;
	rebateType7RebateSituation: string;
	rebateType7LineKOptionOne: string;
	rebateType7LineKOptionTwo: string;
	rebateType7LineKOptionThree: string;
	rebateType7LineKOptionFour: string;
	rebateType7LineKOptionFive: string;
	rebateType7LineKOptionSix: string;
	rebateType7LineKAmount: string;
	rebateType7RentalAmount: string;
	rebateType7ProvincialRentalAmount: string;
	rebateType7TotalRebateAmount: string;
	rebateType9AAnd9BLineO: string;
	rebateType9AAnd9BLandValue: string;
	rebateType9AAnd9BLineQ: string;
	rebateType9AAnd9BLineR: string;
	rebateType9AAnd9BLineS: string;
	rebateType9AAnd9BCalculationOne: string;
	rebateType9AAnd9BCalculationTwo: string;
	rebateType9AAnd9BCalculationAmount: string;
	rebateType9AAnd9BLineU: string;
	rebateType9AAnd9BTotalRebateAmount: string;
	certificationName: string;
	certificationDate: string;
	directDepositBranch: string;
	directDepositInstitution: string;
	directDepositAccount: string;
	directDepositAccountHolder: string;
}
